// components/PrizeboardSection.js - Fixed with Correct Dealership Name
import React, { useRef, useState } from 'react';
import Section from './Section';
import { FileText, Upload, CheckCircle, Clock, User, Mail, ExternalLink } from "lucide-react";

export default function PrizeboardSection({ 
  orderDetails, 
  setOrderDetails, 
  prizeboardFile, 
  isEditing, 
  handleFileUpload,
  dealershipInfo  // Add this prop to access dealership information
}) {
  const prizeboardInputRef = useRef(null);
  const [emailSending, setEmailSending] = useState(false);

  const getStatusIcon = () => {
    if (prizeboardFile) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (orderDetails.sentToTravis) return <Clock className="w-5 h-5 text-yellow-600" />;
    return <Upload className="w-5 h-5 text-gray-400" />;
  };

  const getStatusText = () => {
    if (prizeboardFile) return 'Prizeboard Complete';
    if (orderDetails.sentToTravis) return 'Sent to Travis - Awaiting Prizeboard';
    return 'Prizeboard Pending';
  };

  const getStatusColor = () => {
    if (prizeboardFile) return 'bg-green-100 text-green-800 border-green-200';
    if (orderDetails.sentToTravis) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleSendToDealerApps = async () => {
    if (!prizeboardFile || !orderDetails) {
      alert('Prizeboard file and order details are required');
      return;
    }

    setEmailSending(true);
    
    try {
      // Get dealership name from the correct source with debugging
      const jobNumber = orderDetails.jobNumber || orderDetails.id || 'Unknown';
      
      const dealershipName = dealershipInfo?.name || 'Unknown Dealership';
      
      // If still unknown, try to get from orderDetails as fallback
      const finalDealershipName = dealershipName === 'Unknown Dealership' 
        ? (orderDetails.dealershipName || orderDetails.clientName || 'Unknown Dealership')
        : dealershipName;
      
      const emailSubject = `Job #${jobNumber}: ${finalDealershipName} Prizeboard`;
      const emailBody = `This prizeboard has been marked as approved.\n\nThank you!`;
      const recipientEmail = 'Aja@DealerApps.com';

      const mailtoLink = `mailto:${recipientEmail}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      
      // Open the email client
      window.location.href = mailtoLink;
      
      // Show instructions for attachment
      setTimeout(() => {
        alert(`Email client opened!\n\nPlease manually attach the prizeboard file to the email before sending.\n\nPrizeboard file URL: ${prizeboardFile}`);
      }, 500);

    } catch (error) {
      console.error('Error creating email:', error);
      alert('Failed to create email. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleAdvancedEmailSend = async () => {
    if (!prizeboardFile || !orderDetails) {
      alert('Prizeboard file and order details are required');
      return;
    }

    setEmailSending(true);

    try {
      const jobNumber = orderDetails.jobNumber || orderDetails.id || 'Unknown';
      
      const dealershipName = dealershipInfo?.name || 'Unknown Dealership';
      const finalDealershipName = dealershipName === 'Unknown Dealership' 
        ? (orderDetails.dealershipName || orderDetails.clientName || 'Unknown Dealership')
        : dealershipName;
      
      // For now, copy information to clipboard and show instructions
      const emailInfo = {
        to: 'Aja@DealerApps.com',
        subject: `Job #${jobNumber}: ${finalDealershipName} Prizeboard`,
        body: 'This prizeboard has been marked as approved.\n\nThank you!',
        attachment: prizeboardFile
      };

      // Copy to clipboard
      await navigator.clipboard.writeText(JSON.stringify(emailInfo, null, 2));
      
      // Show detailed instructions
      alert(`Email details copied to clipboard!\n\nTo: Aja@DealerApps.com\nSubject: Job #${jobNumber}: ${finalDealershipName} Prizeboard\nMessage: This prizeboard has been marked as approved. Thank you!\n\nPrizeboard URL: ${prizeboardFile}\n\nYou can download the prizeboard and attach it to your email.`);

    } catch (error) {
      console.error('Error preparing email:', error);
      alert('Failed to prepare email. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="order-card">
      <Section title="Prizeboard Management" defaultOpen={false}>
        <div className="prizeboard-section">
          {/* Status Overview */}
          <div className="prizeboard-status-card">
            <div className="status-header">
              {getStatusIcon()}
              <h3>{getStatusText()}</h3>
            </div>
            <div className={`status-badge ${getStatusColor()}`}>
              {prizeboardFile ? 'Complete' : orderDetails.sentToTravis ? 'In Progress' : 'Pending'}
            </div>
          </div>

          {/* Progress Steps */}
          <div className="prizeboard-progress">
            <div className="progress-step">
              <div className={`step-circle ${orderDetails.sentToTravis ? 'completed' : 'pending'}`}>
                <User className="w-4 h-4" />
              </div>
              <div className="step-content">
                <h4>Send to Travis</h4>
                <p className="step-description">
                  {orderDetails.sentToTravis ? 'Request sent to Travis' : 'Send prizeboard request to Travis'}
                </p>
              </div>
            </div>

            <div className="progress-connector">
              <div className={`connector-line ${prizeboardFile ? 'completed' : 'pending'}`}></div>
            </div>

            <div className="progress-step">
              <div className={`step-circle ${prizeboardFile ? 'completed' : 'pending'}`}>
                <FileText className="w-4 h-4" />
              </div>
              <div className="step-content">
                <h4>Prizeboard Upload</h4>
                <p className="step-description">
                  {prizeboardFile ? 'Prizeboard file uploaded' : 'Awaiting prizeboard file'}
                </p>
              </div>
            </div>
          </div>

          {/* Travis Request Section */}
          <div className="travis-section">
            <h4>Travis Request</h4>
            <div className="travis-controls">
              <label className="travis-checkbox">
                <input
                  type="checkbox"
                  checked={orderDetails.sentToTravis || false}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setOrderDetails((prev) => ({
                      ...prev,
                      sentToTravis: e.target.checked
                    }))
                  }
                />
                <span className="checkmark"></span>
                <span className="label-text">Prizeboard sent to Travis</span>
              </label>
              {orderDetails.sentToTravis && (
                <div className="sent-indicator">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span>Prizeboard sent</span>
                </div>
              )}
            </div>
          </div>

          {/* File Upload Section */}
          <div className="prizeboard-upload">
            <h4>Prizeboard File</h4>
            
            {prizeboardFile ? (
              <div className="file-display">
                <div className="file-info">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div className="file-details">
                    <span className="file-name">Prizeboard File</span>
                    <span className="file-status">Ready for use</span>
                  </div>
                </div>
                <div className="file-actions">
                  <a
                    href={prizeboardFile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-button"
                  >
                    <FileText className="w-4 h-4" />
                    View File
                  </a>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => prizeboardInputRef.current?.click()}
                      className="replace-button"
                    >
                      <Upload className="w-4 h-4" />
                      Replace
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="upload-area-prizeboard">
                <div className="upload-content">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h5>Upload Prizeboard</h5>
                  <p>Drag and drop or click to select the prizeboard file</p>
                  <button
                    type="button"
                    disabled={!isEditing}
                    onClick={() => prizeboardInputRef.current?.click()}
                    className="upload-button"
                  >
                    <Upload className="w-4 h-4" />
                    Choose File
                  </button>
                </div>
              </div>
            )}

            {/* Hidden file input */}
            <input
              type="file"
              ref={prizeboardInputRef}
              disabled={!isEditing}
              hidden
              onChange={(e) => handleFileUpload(e.target.files[0], 'prizeboard')}
            />
          </div>

          {/* Send to DealerApps Section - Only show when prizeboard is uploaded */}
          {prizeboardFile && (
            <div className="dealerapps-section">
              <h4>Send to DealerApps</h4>
              <div className="dealerapps-content">
                <p className="dealerapps-description">
                  Send the approved prizeboard to DealerApps for final processing.
                </p>
                
                <div className="dealerapps-buttons">
                  <button
                    type="button"
                    onClick={handleSendToDealerApps}
                    disabled={emailSending}
                    className="dealerapps-button primary"
                  >
                    <Mail className="w-4 h-4" />
                    {emailSending ? 'Opening Email...' : 'Send to DealerApps'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleAdvancedEmailSend}
                    disabled={emailSending}
                    className="dealerapps-button secondary"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Copy Email Details
                  </button>
                </div>

                <div className="dealerapps-info">
                  <small>
                    <strong>Email will be sent to:</strong> Aja@DealerApps.com<br/>
                    <strong>Subject:</strong> Job #{orderDetails.jobNumber || orderDetails.id}: {dealershipInfo?.name || orderDetails.dealershipName || orderDetails.clientName || 'Unknown Dealership'} Prizeboard<br/>
                    <strong>Message:</strong> This prizeboard has been marked as approved. Thank you!
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="prizeboard-help">
            <div className="help-content">
              <h5>Prizeboard Process</h5>
              <ol>
                <li>Check "Prizeboard request sent to Travis" when prizeboard is sent.</li>
                <li>Travis will create the prizeboard with winning numbers and send back.</li>
                <li>Upload the approved prizeboard file when Will approves.</li>
                <li>Use "Send to DealerApps" to email the approved prizeboard to DealerApps.</li>
                <li>Order can proceed to completion once prizeboard is processed.</li>
              </ol>
            </div>
          </div>
        </div>
      </Section>

      <style jsx>{`
        .dealerapps-section {
          margin-top: 24px;
          padding: 20px;
          background: linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%);
          border: 2px solid #2196f3;
          border-radius: 12px;
        }

        .dealerapps-section h4 {
          margin: 0 0 12px 0;
          color: #1976d2;
          font-weight: 600;
        }

        .dealerapps-description {
          margin-bottom: 16px;
          color: #555;
          font-size: 14px;
        }

        .dealerapps-buttons {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .dealerapps-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: none;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .dealerapps-button.primary {
          background: #2196f3;
          color: white;
        }

        .dealerapps-button.primary:hover:not(:disabled) {
          background: #1976d2;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
        }

        .dealerapps-button.secondary {
          background: white;
          color: #2196f3;
          border: 2px solid #2196f3;
        }

        .dealerapps-button.secondary:hover:not(:disabled) {
          background: #2196f3;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
        }

        .dealerapps-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }

        .dealerapps-info {
          background: rgba(255, 255, 255, 0.8);
          padding: 12px;
          border-radius: 8px;
          border-left: 4px solid #2196f3;
        }

        .dealerapps-info small {
          color: #666;
          line-height: 1.4;
        }

        @media (max-width: 768px) {
          .dealerapps-buttons {
            flex-direction: column;
          }
          
          .dealerapps-button {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}